import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

export function Card({ title, actions, padded, children, ...rest }: { title?: ReactNode; actions?: ReactNode; padded?: boolean; children: ReactNode } & Omit<HTMLAttributes<HTMLElement>, "title">) {
  return (
    <section className={`ds-card${padded ? " ds-card--padded" : ""}`} {...rest}>
      {title || actions ? <header className="ds-card__header"><h3 className="ds-h3">{title}</h3>{actions}</header> : null}
      {children}
    </section>
  );
}

export const StatGrid = ({ children }: { children: ReactNode }) => <div className="ds-stat-grid">{children}</div>;

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="ds-stat">
      <span className="ds-stat__label">{label}</span>
      <strong className="ds-stat__value">{value}</strong>
      {hint ? <span className="ds-stat__hint">{hint}</span> : null}
    </div>
  );
}

export function Notice({ tone = "warning", action, children }: { tone?: "warning" | "danger" | "info"; action?: ReactNode; children: ReactNode }) {
  return (
    <div className={`ds-notice${tone === "warning" ? "" : ` ds-notice--${tone}`}`} role={tone === "danger" ? "alert" : "status"}>
      <span>{children}</span>
      {action}
    </div>
  );
}

export function EmptyState({ title, children }: { title?: string; children?: ReactNode }) {
  return (
    <div className="ds-empty">
      {title ? <strong className="ds-empty__title">{title}</strong> : null}
      {children}
    </div>
  );
}

export type BadgeTone = "neutral" | "accent" | "success" | "warning" | "danger";
export const Badge = ({ tone = "neutral", children }: { tone?: BadgeTone; children: ReactNode }) => <span className={`ds-badge${tone === "neutral" ? "" : ` ds-badge--${tone}`}`}>{children}</span>;

/** Ponto colorido para distinguir categorias (a cor vem dos tokens de categoria). */
export const Dot = ({ color }: { color: string }) => <i className="ds-dot" style={{ "--dot-color": color } as CSSProperties} aria-hidden="true" />;

export const Spinner = ({ label = "Carregando" }: { label?: string }) => <div className="ds-spinner" role="status" aria-label={label} />;

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="ds-page-header">
      <div>
        <h1 className="ds-h1">{title}</h1>
        {subtitle ? <p className="ds-page-header__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="ds-row" data-print="hide">{actions}</div> : null}
    </div>
  );
}

export function Segmented<Value extends string>({ label, value, options, onChange }: { label: string; value: Value; options: { value: Value; label: ReactNode }[]; onChange: (value: Value) => void }) {
  return (
    <div className="ds-segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" className="ds-segmented__item" aria-pressed={option.value === value} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function DescriptionList({ items }: { items: { term: string; value: ReactNode; wide?: boolean }[] }) {
  return (
    <dl className="ds-dl">
      {items.map((item) => (
        <div key={item.term} data-wide={item.wide ? "true" : undefined}>
          <dt>{item.term}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export const Row = ({ align = "start", children, style }: { align?: "start" | "between" | "end"; children: ReactNode; style?: CSSProperties }) => (
  <div className={`ds-row${align === "start" ? "" : ` ds-row--${align}`}`} style={style}>{children}</div>
);
export const Stack = ({ children }: { children: ReactNode }) => <div className="ds-stack">{children}</div>;
